import { computed, inject, Injectable, signal } from '@angular/core';
import { BiometricService } from '../biometric/biometric.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { NavService } from '@shared/services/nav/nav.service';
import { StorageService } from '@shared/services/storage/storage.service';
import { LoadingService } from '@shared/services/loading/loading.service';
import { ToastrService } from '@shared/services/toastr/toastr.service';
import { TranslateService } from '@shared/translate/translate.service';
import { ScreenResizeService } from '@shared/services/screen.resize/screen-resize.service';
import { TokenService } from '../token/token.service';
import { Credenciales, DataBase, StoredUser, UsuarioAuthenticado, UsuarioDB } from '@shared/models/login-response.model';
import { RolService } from '@shared/services/rol/rol.service';
import { FiltroClienteService } from '@shared/services/filtro-cliente/filtro-cliente.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly _biometrico = inject(BiometricService);
  private readonly _auth = inject(AuthenticationService);
  private readonly _nav = inject(NavService);
  private readonly _storage = inject(StorageService);
  private readonly _loading = inject(LoadingService);
  private readonly _toastr = inject(ToastrService);
  private readonly _translate = inject(TranslateService);
  private readonly _screen = inject(ScreenResizeService);
  private readonly _token = inject(TokenService);
  private readonly _rol = inject(RolService);
  private readonly _filtroCliente = inject(FiltroClienteService);

  readonly isNative = computed(() => this._screen.isNative());

  readonly listaDB = signal<DataBase[]>([]);

  readonly isValidLogin = signal<boolean>(false);

  async checkearCredencialesGuardadas(): Promise<void> {
    const flag = await this._storage.getItem('biometricEnabled');
    if (flag !== 'true') {
      console.info('ℹ️ Biometría no activada por el usuario');
      return;
    }

    try {
      const credenciales = await this.obtenerCredencialesBiometricas();

      if (credenciales) {
        await this.login(credenciales);
        console.info('🟢 Acceso automático con biometría');
      } else {
        console.warn('⚠️ No se encontraron credenciales después de la verificación');
      }
    } catch (error) {
      console.error('❌ Error en acceso biométrico:', error);
    }
  }

  async accesoBiometrico(): Promise<void> {
    try {
      const credenciales = await this.obtenerCredencialesBiometricas();
      if (!credenciales) {
        console.warn('⚠️ No se encontraron credenciales almacenadas');
        this._toastr.error(this._translate.instant('xNoSeEncontraronCredenciales'));
        return;
      }

      await this.login(credenciales);
    } catch (error) {
      console.error('❌ Error en acceso biométrico:', error);
    }
  }

  async verificarCredenciales(cred: Credenciales): Promise<void> {
    const credencialesGuardadas = await this._biometrico.getCredentials();

    const requierenValidacion = !credencialesGuardadas || this.diferenciaEnCredenciales(credencialesGuardadas, cred);

    if (requierenValidacion) {
      try {
        await this._biometrico.biometricVerification();
        await this.login(cred);
      } catch (error) {
        console.error('❌ Error en verificación biométrica:', error);
      }
    }
  }

  private async obtenerCredencialesBiometricas(): Promise<Credenciales | null> {
    const secureCredentials = await this._biometrico.getSecureCredentials();
    if (secureCredentials) {
      console.info('🟢 Credenciales biométricas seguras obtenidas');
      return secureCredentials;
    }

    try {
      await this._biometrico.biometricVerification();
      console.info('🟢 Verificación biométrica (modo compatibilidad) exitosa');
      return await this._biometrico.getCredentials();
    } catch (error) {
      const errorMessage = `${error}`;
      if (errorMessage.includes('Biometric crypto object unavailable')) {
        const legacyCredentials = await this._biometrico.getCredentials();
        if (legacyCredentials) {
          this._toastr.error('Reingrese con usuario y clave para reconfigurar el acceso biométrico.');
          console.warn('⚠️ Credenciales biométricas en formato anterior detectadas. Requieren reconfiguración.');
          return null;
        }
      }
      throw error;
    }
  }

  private async guardarCredenciales(cred: Credenciales): Promise<void> {
    try {
      await this._biometrico.setCredentials(cred);
      await this._storage.setItem('biometricEnabled', 'true');
      console.info('🟢 Credenciales guardadas correctamente');
    } catch (error) {
      console.error('❌ Error al guardar las credenciales:', error);
    }
  }

  private diferenciaEnCredenciales(guardada: Credenciales, nueva: Credenciales): boolean {
    return guardada.email !== nueva.email || guardada.password !== nueva.password;
  }

  async login(cred: Credenciales, guardar: boolean = false): Promise<void> {
    const load = await this._loading.show('xIniciandoSesion');

    this._auth.login(cred).subscribe({
      next: async ({ idUsuario, apellidoYNombre, perfil, token, refresh_token, siglaIdioma, lstPosiblesBases }) => {
        this.guardarUsuario({ idUsuario, apellidoYNombre, perfil, token, refresh_token, siglaIdioma });

        if (lstPosiblesBases.length === 1) {
          this.seleccionarBaseDatos(lstPosiblesBases[0]);
        } else {
          this.listaDB.set(lstPosiblesBases);
          this._nav.seleccionBaseDatos();
        }

        this._toastr.success(this._translate.instant('xSesionIniciada'));
        if (guardar) await this.guardarCredenciales(cred);
      },
      error: (err) => {
        console.error('❌ Error al iniciar sesión:', err);
        load.dismiss();
      },
      complete: () => load.dismiss(),
    });
  }

  async seleccionarBaseDatos(db: DataBase): Promise<void> {
    const loading = await this._loading.show('xDbLoading');

    this._auth.seleccionarBase(db).subscribe({
      next: ({ user }) => {
        this.guardarDataBase({ ...user, db });
        this._rol.getCurrentRol();
        loading.dismiss();
      },
      error: () => {
        loading.dismiss();
      },
    });
  }

  async solicitarNuevoPassword(email: string): Promise<void> {
    const load = await this._loading.show('xEnviandoSolicitud');

    this._auth.recoveryPass({ email }).subscribe({
      next: (mensaje) => {
        this._toastr.success(mensaje);
        this.volverLogin();
      },
      error: () => load.dismiss(),
      complete: () => load.dismiss(),
    });
  }

  private async guardarUsuario({ idUsuario, apellidoYNombre, perfil, token, refresh_token, siglaIdioma }: Omit<UsuarioAuthenticado, 'lstPosiblesBases'>) {
    const storedUser = new StoredUser({
      idUsuario,
      apellidoYNombre,
      perfil,
    });
    this._storage.setItem('token', token);
    this._storage.setItem('refresh_token', refresh_token);
    this._storage.setItem('lang', siglaIdioma);
    this._storage.setItem('user', storedUser);

    const message = `${this._translate.instant('xBienvenido')} ${apellidoYNombre}`;
    this._toastr.success(message);

    setTimeout(() => this._token.loadToken());
  }

  private guardarDataBase({ token, lstSociedades, lstClientes, refresh_token, db }: UsuarioDB & { db: DataBase }): void {
    this._storage.setItem('token', token);
    this._storage.setItem('sociedades', lstSociedades);
    this._storage.setItem('clientes', lstClientes);
    this._storage.setItem('db', db);
    this._storage.setItem('token', token);
    this._storage.setItem('refresh_token', refresh_token);

    this.isValidLogin.set(true);

    this._rol.getCurrentRol();

    this._token.loadToken();
    setTimeout(() => this._nav.volverInicio());
  }

  refreshToken() {
    return this._auth.refreshToken();
  }

  async logout(): Promise<void> {
    this._storage.removeItem('token');
    this._storage.removeItem('refresh_token');
    this._storage.removeItem('user');
    this._storage.removeItem('sociedad');
    this._storage.removeItem('clientes');
    this._storage.removeItem('db');

    this._token.token.set('');

    this.isValidLogin.set(false);
    this._filtroCliente.logout();

    this._nav.volverLogin();
  }

  ingresarConCredenciales() {
    this._nav.formularioLogin();
  }

  volverLogin() {
    this._nav.volverLogin();
  }

  volverInicio() {
    this._nav.volverInicio();
  }

  irPassRecovery() {
    this._nav.irRecuperarPassword();
  }
}
